import {
  Activity,
  BarChart3,
  DollarSign,
  Repeat,
  Wallet,
  FileSearch,
  Gauge,
  LineChart,
  MapPin,
  PenLine,
  SearchCheck,
  Sparkles,
  Wrench,
  Globe2,
} from "lucide-react";
import type { ComponentType } from "react";

/**
 * The left half of the login and signup pages (desktop): what Rankbist does,
 * in the same dark "night" look as the admin sign-in page. It is a description
 * of the product, not a result: nothing here claims a number.
 */

type Icon = ComponentType<{ size?: number; className?: string }>;

/** The white wordmark for dark surfaces. Draws the mark and name if the image can't load. */
export function BrandMark({ className = "h-7" }: { className?: string }) {
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src="/rankbist.png"
      alt="Rankbist"
      width={1781}
      height={375}
      className={`${className} w-auto brightness-0 invert`}
    />
  );
}

export type ShowcaseMode = "login" | "signup" | "affiliate" | "affiliate-login";

const COPY: Record<
  ShowcaseMode,
  {
    title: string;
    accent: string;
    lede: string;
    points: { icon: Icon; title: string; body: string }[];
  }
> = {
  login: {
    title: "Access the world's #1",
    accent: "fully automated SEO engine.",
    lede: "Your checks, audits, rankings and reports are waiting in your dashboard.",
    points: [
      {
        icon: FileSearch,
        title: "Your checks and reports",
        body: "Every page you've checked, saved and ready to open again.",
      },
      {
        icon: LineChart,
        title: "Rankings from your own data",
        body: "Follow the keywords you rank for, straight from Search Console.",
      },
      {
        icon: Wrench,
        title: "Fixes waiting for review",
        body: "Suggested fixes are drafted for you. Nothing changes until you approve it.",
      },
    ],
  },
  signup: {
    title: "Access the world's #1",
    accent: "fully automated SEO engine.",
    lede: "Check content, audit your site, follow your own Search Console rankings and see how AI answers mention you, all in one place.",
    points: [
      {
        icon: FileSearch,
        title: "Check any page",
        body: "Content, readability, links and images, checked against 30+ real factors.",
      },
      {
        icon: LineChart,
        title: "Follow your rankings",
        body: "Your own Search Console data, tracked keyword by keyword.",
      },
      {
        icon: Sparkles,
        title: "See how AI mentions you",
        body: "Find out whether AI answers bring up your site, and where.",
      },
    ],
  },
  affiliate: {
    title: "Earn recurring income",
    accent: "promoting Rankbist.",
    lede: "Share your link and earn a commission on every package your referrals buy, upgrade or renew, for life.",
    points: [
      { icon: DollarSign, title: "Commission on the first purchase", body: "On every customer who buys a package through your link." },
      { icon: Repeat, title: "Recurring on renewals and upgrades", body: "For as long as they stay a customer, for life." },
      { icon: Wallet, title: "Payouts to Binance, bank or PayPal", body: "Request a withdrawal from your portal once you pass the minimum." },
    ],
  },
  "affiliate-login": {
    title: "Welcome back to",
    accent: "Rankbist Affiliates.",
    lede: "Your link, visits, sign-ups, earnings and payouts are waiting in your portal.",
    points: [
      { icon: BarChart3, title: "Visits, sign-ups and buyers", body: "Every number from your link, in one place." },
      { icon: DollarSign, title: "Every commission listed", body: "What was paid, the rate, and when it becomes withdrawable." },
      { icon: Wallet, title: "Withdraw in a few clicks", body: "To Binance, your bank or PayPal." },
    ],
  },
};

/* Tool names drifting up the left side: what the account includes. Three per row so
 * each line reads as a sentence-length strip (6-9 words), wide enough to be noticed. */
const FEED: { icon: Icon; text: string }[] = [
  { icon: SearchCheck, text: "Google index checker" },
  { icon: Gauge, text: "Page speed and Lighthouse" },
  { icon: Globe2, text: "Site audit" },
  { icon: PenLine, text: "Article writer" },
  { icon: LineChart, text: "Search Console rankings" },
  { icon: Sparkles, text: "AI mentions" },
  { icon: Activity, text: "Rank tracker" },
  { icon: FileSearch, text: "Content checks" },
  { icon: MapPin, text: "Local SEO" },
  { icon: Wrench, text: "Fixes for review" },
  { icon: Gauge, text: "Readability and links" },
  { icon: SearchCheck, text: "Competitor research" },
];
const PER_ROW = 3;
const ROWS = Array.from({ length: FEED.length / PER_ROW }, (_, i) =>
  FEED.slice(i * PER_ROW, i * PER_ROW + PER_ROW),
);

export function AuthShowcase({ mode }: { mode: ShowcaseMode }) {
  const c = COPY[mode];
  return (
    <section className="hidden lg:block">
      <h1 className="rba-title max-w-[22ch] text-[1.65rem] sm:text-[44px] font-extrabold leading-[1.08] tracking-[-0.03em]">
        {c.title}{" "}
        <span className="hero-accent on-dark">
          {c.accent}
        </span>
      </h1>
      <p className="rba-lede mt-4 max-w-[46ch] text-[0.9rem] sm:text-[15.5px] leading-relaxed text-white/65">
        {c.lede}
      </p>

      <ul className="mt-9 grid max-w-[560px] gap-3">
        {c.points.map((p, i) => (
          <li
            key={p.title}
            className="rbl-in flex items-start gap-3.5 rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10"
            style={{ animationDelay: `${0.15 + i * 0.12}s` }}
          >
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#8f78ff] to-[#4b33e0] shadow-[0_10px_24px_-10px_rgba(106,82,255,0.9)]">
              <p.icon size={18} />
            </span>
            <span>
              <span className="block text-[14.5px] font-bold">{p.title}</span>
              <span className="mt-0.5 block text-[13px] leading-snug text-white/60">
                {p.body}
              </span>
            </span>
          </li>
        ))}
      </ul>

      <div
        className="rbl-feed-mask rba-feed mt-8 h-[140px] max-w-[560px] overflow-hidden"
        aria-hidden
      >
        <div className="rbl-feed flex flex-col gap-2.5 pb-2.5">
          {[...ROWS, ...ROWS].map((row, r) => (
            <div key={r} className="flex flex-wrap gap-2.5">
              {row.map((f) => (
                <span
                  key={f.text}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-white/[0.07] px-3.5 py-2 text-[13px] font-semibold text-white/85 ring-1 ring-white/10"
                >
                  <span className="rbl-dot h-1.5 w-1.5 rounded-full bg-[#34d399]" />
                  <f.icon size={14} className="text-[#C9B8FF]" />
                  {f.text}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
